import { FileText, Music, File as FileIcon, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useMediaObjectUrl } from '@/hooks/useMediaObjectUrl';
import { publicUrl, type MediaItem } from '@/lib/media';
import { sanitizeUrl } from '@/lib/sanitizeUrl';

interface MediaPreviewProps {
  item: MediaItem;
}

/**
 * Full-size playback/preview for one item, used in the detail dialog.
 *
 * Prefers the local blob so previewing works offline and costs no
 * bandwidth, falling back to the hosted URL when the bytes have been
 * evicted. Every remote URL passes through `sanitizeUrl()` — these
 * ultimately originate from a server response, so they're treated as
 * untrusted input like any other external URL in the app.
 */
export function MediaPreview({ item }: MediaPreviewProps) {
  const { url: localUrl, isLoading } = useMediaObjectUrl(item.sha256, 'blob');
  const remote = sanitizeUrl(publicUrl(item));
  const src = localUrl ?? remote;

  if (isLoading) {
    return <Skeleton className="h-56 w-full rounded-lg" />;
  }

  if (!src) {
    return (
      <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-lg border border-dashed text-sm text-muted-foreground">
        <FileIcon className="size-6" />
        No local copy and no uploaded URL to preview.
      </div>
    );
  }

  if (item.kind === 'image') {
    return (
      <img
        src={src}
        alt={item.alt ?? ''}
        className="max-h-72 w-full rounded-lg bg-muted object-contain"
      />
    );
  }

  if (item.kind === 'video') {
    return <video src={src} controls className="max-h-72 w-full rounded-lg bg-black" />;
  }

  if (item.kind === 'audio') {
    return (
      <div className="flex flex-col items-center gap-3 rounded-lg border bg-muted/20 p-6">
        <Music className="size-8 text-muted-foreground" />
        <audio src={src} controls className="w-full" />
      </div>
    );
  }

  // PDFs and anything else: an inline viewer would mean embedding
  // untrusted content in an iframe, so offer an explicit new-tab link
  // instead. Only remote https URLs are linked; a local blob URL is
  // opened the same way but can't be rendered in place safely either.
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border bg-muted/20 p-6 text-center">
      {item.kind === 'pdf' ? (
        <FileText className="size-8 text-muted-foreground" />
      ) : (
        <FileIcon className="size-8 text-muted-foreground" />
      )}
      <p className="text-sm text-muted-foreground">{item.name}</p>
      <Button type="button" variant="outline" size="sm" asChild>
        <a href={src} target="_blank" rel="noopener noreferrer">
          <ExternalLink className="size-4" />
          Open in a new tab
        </a>
      </Button>
    </div>
  );
}
